'use client';

import { c as _c } from "react/compiler-runtime";
import { Drawer, Heading, Icon, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useEffect, useId, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { OptimizelyShipMeter, useCustomerAutoshipSubscriptions, useDecisions } from '@libs/common-next';
import { CartItems } from './CartItems';
import { Footer } from './Footer';
import { useCartConfirmDrawer } from './model/useCartConfirmDrawer';
import './CartConfirmDrawer.scss';
type CartConfirmDrawerProps = {
  /** Hide the Sparky overlay when this drawer is stacked over another drawer. */
  stacked?: boolean;
  /** When the host drawer is closed, the stacked cart confirm drawer should close too. */
  forceClosed?: boolean;
  /** Sparky close (X / overlay / Esc). Used when stacked over another drawer. */
  onCloseAll?: () => void;
};
export const CartConfirmDrawer = t0 => {
  const $ = _c(31);
  const {
    stacked: t1,
    forceClosed: t2,
    onCloseAll
  } = t0 === undefined ? {} : t0;
  const stacked = t1 === undefined ? false : t1;
  const forceClosed = t2 === undefined ? false : t2;
  const {
    autoshipData,
    isReady: isAutoshipCustomerStatusReady
  } = useCustomerAutoshipSubscriptions();
  const hasFreeShipMeter = useDecisions("x_pop_sla");
  const {
    isOpen,
    isStacked,
    close,
    content,
    items
  } = useCartConfirmDrawer(useShallow(_temp));
  const headingId = useId();
  const [hasBeenRendered, setHasBeenRendered] = useState(false);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setHasBeenRendered(true);
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  useEffect(() => {
    if (forceClosed && stacked && isStacked) {
      close();
    }
  }, [forceClosed, stacked, isStacked, close]);
  let t5;
  if ($[2] !== items) {
    t5 = items.find(_temp2);
    $[2] = items;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const autoshipItem = t5;
  const isExistingAutoshipCustomer = Boolean(autoshipData?.data?.hasAutoship);
  if (!hasBeenRendered) {
    return null;
  }
  const handleClose = () => {
    close();
    onCloseAll?.();
  };
  const isActive = stacked ? isOpen && isStacked : isOpen && !isStacked;
  const T0 = Drawer;
  const t6 = "right";
  const t7 = "added-to-cart-drawer";
  const t8 = cn("added-to-cart-drawer", {
    "added-to-cart-drawer--stacked": stacked
  });
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon name="check-circle-fill" />;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const t10 = content.title ?? "Added to cart";
  let t11;
  if ($[5] !== headingId || $[6] !== t10) {
    t11 = <Drawer.Header>{t9}<Heading id={headingId} tagName="h2" size="headline">{t10}</Heading></Drawer.Header>;
    $[5] = headingId;
    $[6] = t10;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  let t12;
  if ($[8] !== content.subtitle) {
    t12 = content.subtitle && <TextPassage size="lg" className="added-to-cart-drawer__subtitle">{content.subtitle}</TextPassage>;
    $[8] = content.subtitle;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <CartItems />;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== autoshipItem || $[12] !== hasFreeShipMeter || $[13] !== isActive || $[14] !== isAutoshipCustomerStatusReady || $[15] !== isExistingAutoshipCustomer) {
    t14 = isActive && hasFreeShipMeter && autoshipItem && isAutoshipCustomerStatusReady && !isExistingAutoshipCustomer && <OptimizelyShipMeter autoshipFrequency={Number(autoshipItem.autoshipFrequency)} autoshipFrequencyUnitOfMeasure={Number(autoshipItem.autoshipFrequencyUnitOfMeasure)} />;
    $[11] = autoshipItem;
    $[12] = hasFreeShipMeter;
    $[13] = isActive;
    $[14] = isAutoshipCustomerStatusReady;
    $[15] = isExistingAutoshipCustomer;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t12 || $[18] !== t14) {
    t15 = <Drawer.Body>{t12}{t13}{t14}</Drawer.Body>;
    $[17] = t12;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== onCloseAll) {
    t16 = <Drawer.Footer><Footer onCloseAll={onCloseAll} /></Drawer.Footer>;
    $[20] = onCloseAll;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== T0 || $[23] !== handleClose || $[24] !== headingId || $[25] !== isActive || $[26] !== t11 || $[27] !== t15 || $[28] !== t16 || $[29] !== t8) {
    t17 = <T0 align={t6} isActive={isActive} onClose={handleClose} data-testid={t7} className={t8} ariaLabelledBy={headingId} dismissible={true}>{t11}{t15}{t16}</T0>;
    $[22] = T0;
    $[23] = handleClose;
    $[24] = headingId;
    $[25] = isActive;
    $[26] = t11;
    $[27] = t15;
    $[28] = t16;
    $[29] = t8;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
};
function _temp(store) {
  return {
    isOpen: store.isOpen,
    isStacked: store.stacked,
    close: store.close,
    content: store.content,
    items: store.items
  };
}
function _temp2(item) {
  return item.autoshipFrequency != null && item.autoshipFrequencyUnitOfMeasure != null;
}